/**
 * Static configuration tables for {@link SidebarRail}.
 *
 * Extracted verbatim from SidebarRail.tsx (zero behavior change) so the
 * orchestrator file holds control flow, not lookup data.
 */

/** Maps rail item keys → module types they match. */
export const RAIL_MODULE_TYPES: Record<string, string[]> = {
  email: ['email'],
  calendar: ['event_list'],
  tasks: ['task_list'],
  contacts: ['contact_list'],
  clients: ['client_list'],
  files: ['drive', 'file_viewer', 'docs', 'note_grid', 'photos', 'gallery'],
  places: ['places', 'place_list'],
  stats: ['stat_list', 'stat_chart'],
  pages: ['pages', 'report', 'html_viewer'],
  notes: ['notes'],
  health: ['health'],
  channels: ['channels'],
  agents: ['agents'],
  synthesis: ['synthesis'],
};

/** Default sidebar visibility — true = shown by default. */
export const DEFAULT_VISIBILITY: Record<string, boolean> = {
  email: true,
  calendar: true,
  drive: true,
  tasks: true,
  contacts: true,
  clients: false,
  places: true,
  pages: true,
  notes: true,
  health: false,
  news: true,
  channels: false,
  shopify: false,
  mixpanel: false,
  agents: true,
  synthesis: true,
};

/** Module type to create when a checklist item is checked and no module exists. */
export const CHECKLIST_MODULE_TYPES: Record<string, { type: string; label: string }> = {
  email: { type: 'gmail', label: 'Email' },
  calendar: { type: 'event_list', label: 'Calendar' },
  drive: { type: 'drive', label: 'Drive' },
  tasks: { type: 'task_list', label: 'Kanban' },
  contacts: { type: 'contact_list', label: 'Contacts' },
  clients: { type: 'client_list', label: 'Clients' },
  places: { type: 'places', label: 'Places' },
  pages: { type: 'pages', label: 'Pages' },
  notes: { type: 'notes', label: 'Notes' },
  health: { type: 'health', label: 'Health' },
  news: { type: 'news', label: 'News' },
  channels: { type: 'channels', label: 'Channels' },
  shopify: { type: 'shopify-crm', label: 'Shopify CRM' },
  mixpanel: { type: 'mixpanel', label: 'Mixpanel' },
  agents: { type: 'agents', label: 'Agents' },
  synthesis: { type: 'synthesis', label: 'Synthesis' },
};

/** All module types claimed by a core rail key. */
export const ALL_CORE_MODULE_TYPES = new Set(Object.values(RAIL_MODULE_TYPES).flat());

/** Flyout display titles per rail key. */
export const RAIL_LABELS: Record<string, string> = {
  email: 'EMAIL',
  calendar: 'CALENDARS',
  tasks: 'TASKS',
  contacts: 'CONTACTS',
  files: 'DRIVE',
  places: 'PLACES',
  stats: 'STATS',
  pages: 'PAGES',
  notes: 'NOTES',
  health: 'HEALTH',
  agents: 'AGENTS',
  synthesis: 'SYNTHESIS',
};

/** Rail items backed by plugins instead of modules. */
export const RAIL_PLUGIN_IDS: Record<string, string> = {
};

/** Maps rail keys to checklist/visibility keys where they differ (files → drive). */
export const railKeyToVisibilityKey: Record<string, string> = { files: 'drive' };

/**
 * Rail keys for the PRIVATE / provider-backed modules (per-member data: mail,
 * calendar, drive). In a TEAM workspace these stay in the top "MODULES" group;
 * everything else moves under a "TEAM MODULES" header (shared Xavior-native data).
 * Note: Drive's rail key is `files`.
 */
export const PROVIDER_RAIL_KEYS = new Set(['email', 'calendar', 'files']);

/**
 * Rail keys for TEAM-ONLY modules — never shown in a solo (Free/Pro) workspace,
 * only in a team workspace (where they appear under "TEAM MODULES").
 */
export const TEAM_ONLY_RAIL_KEYS = new Set(['channels']);

/**
 * Default module-rail order for a workspace with no custom (persisted) order.
 * AI entry points (Agents, Synthesis) lead, then provider modules (Email,
 * Calendar, Drive), then the native data modules. In a team workspace the
 * provider trio is pulled into the top "MODULES" group, so Agents/Synthesis
 * lead the "TEAM MODULES" group. Note: Drive's rail key is `files`.
 */
export const DEFAULT_RAIL_ORDER: readonly string[] = [
  'agents', 'synthesis', 'email', 'calendar', 'files', 'contacts', 'channels', 'tasks', 'notes', 'pages', 'places',
];

/** Module types that should only exist once per workspace (like Drive). */
export const SINGLETON_MODULE_TYPES = new Set(['drive', 'places', 'pages', 'notes', 'gmail', 'event_list', 'sidebar_settings']);
